import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { SpecialRoutingService } from 'src/app/services/special-routing/special-routing.service';
import { environment } from 'src/environments/environment';
import { ListsDialogsComponent } from '../lists-dialogs/lists-dialogs.component';
import { ListsService } from 'src/app/services/lists/lists.service';


export interface List {
  id: number;
  list_name: string;
  shared_with_count: number;
  creation_date: Date;
  last_modified: Date;
  charts_count: number;
  chart_ids: number[];
  chart_rcl_ids: number[];
  original_chart_pack: string | null;
  owner: any;
}

@Component({
  selector: 'app-lists-table',
  templateUrl: './lists-table.component.html',
  styleUrls: ['./lists-table.component.scss'],
})
export class ListsTableComponent {
  @Input() data: List[] = [];
  @Input() tableRowType: string = 'owned_lists';
  @Output() renameListFunction = new EventEmitter<{}>();

  @ViewChild('ownedListsTemplate') ownedListsTemplate!: TemplateRef<any>;
  @ViewChild('sharedListsTemplate') sharedListsTemplate!: TemplateRef<any>;

  templates: { [key: string]: TemplateRef<any> } = {};

  displayedColumns: string[] = ['ID', 'name'];
  dataSource = new MatTableDataSource<List>(this.data);

  displayRenameDialogOwned = false;
  displayRenameDialogShared = false;
  displayShareDialogOwned = false;
  displayShareDialogShared = false;

  displayDeleteDialogOwned = false;
  displayUnShareDialogShared = false;

  public isStaff = localStorage.getItem('staff') === 'true';
  public newListName: string = '';

  errorMessage: string = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    public specialRoutingService: SpecialRoutingService,
    private listService: ListsService
  ) {}

  ngAfterViewInit() {
    this.templates['owned_lists'] = this.ownedListsTemplate;
    this.templates['shared_lists'] = this.sharedListsTemplate;
    this.cdr.detectChanges(); // Manually trigger change detection
  }

  switchTemplate(type: string) {
    this.tableRowType = type;
  }

  selectList(list: List) {
    // Check if the list is valid
    if (!list || !list.id) {
      console.error('Invalid list selected');
      return;
    }

    const id = list.id; // Directly use the list ID

    // Prepare the route with the ID as a query parameter
    const route = this.specialRoutingService.prepareRoute(
      '/saved-charts',
      undefined,
      id
    );

    // Redirect to the route
    this.specialRoutingService.redirectToRoute(route);

    // Log the selected list information
  }

  copyList(list: List) {
    // Check if environment variable is defined
    if (!environment.frontend_base) {
      console.error('Frontend base URL is not defined in the environment.');
      alert('Error: Frontend base URL is not defined.');
      return;
    }

    // Construct the URL to copy
    const urlToCopy = `${environment.frontend_base}saved-charts?id=${list.id}`;

    // Validate URL (optional, but good for checking)
    try {
      new URL(urlToCopy); // This will throw if the URL is invalid
    } catch (error) {
      console.error('Invalid URL:', urlToCopy);
      alert('Failed to create a valid URL.');
      return;
    }

    // Use the Clipboard API to copy the text
    navigator.clipboard
      .writeText(urlToCopy)
      .then(() => {
        console.log('List details copied to clipboard');
        // Notify the user that the copy was successful
        alert('List URL copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        alert('Failed to copy list URL.');
      });
  }

  renameList(list_id: number, data: { name: string }) {

    const { name } = data;

    this.listService.renameList(list_id, name).subscribe(
      (response) => {
        console.log('List created successfully');

        console.log(list_id);

        // Toggle the dialog visibility
        this.toggleRenameDialogOwned();

        // Reload the page to refresh the lists
        window.location.reload();
      },
      (error) => {
        console.error('Error creating list:', error.error.error);
        // Handle the error response here (e.g., display an error message to the user)
      }
    );
  }

  shareList(list_id: number, data: { email: string, editor_permission: boolean }) {
    const { email, editor_permission } = data;
    const email_web_link_domain = environment.frontend_base;
    this.listService
      .shareList(list_id, email, email_web_link_domain, editor_permission)
      .subscribe(
        (response) => {
          console.log('List shared successfully');

          // Toggle the dialog visibility
          this.toggleShareDialogOwned();

          // Reload the page to refresh the lists
          window.location.reload();
        },
        (error) => {
          this.errorMessage =
            error.error.error || 'An error occurred while sharing the list.';
          console.error('Error creating list:', error);
          // Handle the error response here (e.g., display an error message to the user)
        }
      );
  }

  unShareList(list_id: number, data: { email: string }) {
    const { email } = data;

    this.listService.unShareList(list_id, email).subscribe(
      (response) => {
        console.log('List unshared successfully');

        // Toggle the dialog visibility
        this.toggleUnSharedDialogShared();

        // Reload the page to refresh the lists
        window.location.reload();
      },
      (error) => {
        this.errorMessage =
          error.error.error || 'An error occurred while sharing the list.';
        console.error('Error creating list:', error);
        // Handle the error response here (e.g., display an error message to the user)
      }
    );
  }

  deleteList(list_id: number) {
    this.listService.deleteList(list_id).subscribe(
      (response) => {
        console.log(`List ${list_id} deleted successfully`);

        // Toggle the dialog visibility
        this.toggleDeleteDialogOwned();

        // Reload the page to refresh the lists
        window.location.reload();
      },
      (error) => {
        console.error('Error creating list:', error.error.error);
        // Handle the error response here (e.g., display an error message to the user)
      }
    );
  }

  toggleRenameDialogOwned() {
    this.displayRenameDialogOwned = !this.displayRenameDialogOwned;
  }

  toggleRenameDialogShared() {
    this.displayRenameDialogShared = !this.displayRenameDialogShared;
  }

  toggleShareDialogOwned() {
    this.displayShareDialogOwned = !this.displayShareDialogOwned;
  }

  toggleSharedDialogShared() {
    this.displayShareDialogShared = !this.displayShareDialogShared;
  }

  toggleDeleteDialogOwned() {
    this.displayDeleteDialogOwned = !this.displayDeleteDialogOwned;
  }

  toggleUnSharedDialogShared() {
    this.displayUnShareDialogShared = !this.displayUnShareDialogShared;
  }
}

